<template>
  <div class="car-pricing-breakdown-modal">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <b-loading :is-full-page="false" :active="loading"></b-loading>
    </div>

    <!-- Error State -->
    <div v-else-if="hasError" class="error-container">
      <div class="error-message">
        <b-icon icon="alert-circle" size="is-medium"></b-icon>
        <p>Rate details unavailable</p>
        <button class="btn-close-error" @click="$emit('close')">Close</button>
      </div>
    </div>

    <!-- Content -->
    <div v-else class="modal-content">
      <!-- Header -->
      <header class="pricing-header">
        <div class="header-left">
          <div>
            <span v-if="vendorLogo"><img :src="vendorLogo" class="vendor-logo-small" :alt="vendorName" /></span>
            <span v-else class="vendor-name-small">{{ vendorName }}</span>
          </div>
          <span class="header-title">Full pricing breakdown</span>
        </div>
        <span class="header-dates">{{ formattedDateRange }} · {{ rentalDays }} day{{ rentalDays !== 1 ? 's' : '' }}</span>
        <button type="button" class="delete" @click="$emit('close')" />
      </header>

      <!-- Scrollable Body -->
      <div class="pricing-body">
        <!-- Base Rate Section -->
        <section class="pricing-section">
          <p class="section-label">Base rate</p>
          <div class="pricing-row">
            <span class="row-label">Daily rate</span>
            <span class="row-value">{{ formattedDailyRate }} / day</span>
          </div>
          <div class="pricing-row">
            <span class="row-label">Duration</span>
            <span class="row-value">{{ rentalDays }} days</span>
          </div>
          <div class="section-divider"></div>
          <div class="pricing-row subtotal">
            <span>Subtotal</span>
            <span>{{ formattedSubtotal }}</span>
          </div>
        </section>

      <!-- Surcharges Section -->
        <section v-if="surchargeItems.length > 0" class="pricing-section">
          <p class="section-label">Surcharges</p>
          <div v-for="(surcharge, index) in surchargeItems" :key="'surcharge-' + index" class="pricing-row">
            <span class="row-label">{{ surcharge.name }}</span>
            <span class="row-value">{{ surcharge.formattedAmount }}</span>
          </div>
          <div class="section-divider"></div>
          <div class="pricing-row subtotal">
            <span>Surcharges subtotal</span>
            <span>{{ formattedSurchargesSubtotal }}</span>
          </div>
        </section>

        <!-- Optional Add-ons Section (Collapsible) -->
        <section v-if="addOnItems.length > 0" class="pricing-section collapsible-section">
          <div class="section-header-row" @click="addOnsExpanded = !addOnsExpanded">
            <p class="section-label">Optional add-ons ({{ addOnItems.length }})</p>
            <button type="button" class="toggle-btn" :class="{ expanded: addOnsExpanded }">
              <b-icon :icon="addOnsExpanded ? 'chevron-up' : 'chevron-down'" size="is-small"></b-icon>
              <span>{{ addOnsExpanded ? 'Hide' : 'Show' }} details</span>
            </button>
          </div>
          <transition name="collapse">
            <div v-show="addOnsExpanded" class="collapsible-content">
              <div v-for="(addon, index) in addOnItems" :key="'addon-' + index" class="pricing-row">
                <span class="row-label">{{ addon.name }}</span>
                <span class="row-value">{{ addon.formattedPrice }}</span>
              </div>
            </div>
          </transition>
          <div class="section-divider"></div>
          <div class="pricing-row subtotal">
            <span>Add-ons subtotal</span>
            <span>{{ formattedAddOnsSubtotal }}</span>
          </div>
        </section>
      </div>

      <!-- Taxes Section -->
      <section class="pricing-section">
        <p class="section-label">Tax Information</p>
        <template v-if="taxItems.length > 0">
          <div v-for="(tax, index) in taxItems" :key="'tax-' + index" class="pricing-row">
            <div class="row-label-with-note">
              <span class="row-label">{{ tax.name }}</span>
              <span v-if="tax.note" class="row-note">{{ tax.note }}</span>
            </div>
            <span class="row-value">{{ tax.formattedAmount }}</span>
          </div>
        </template>
        <div v-else class="pricing-row">
          <span class="row-label">Estimated taxes & fees</span>
          <span class="row-value">{{ formattedTaxes }}</span>
        </div>
        <div class="section-divider"></div>
        <div class="pricing-row subtotal">
          <span>Taxes  subtotal</span>
          <span>{{ formattedTaxesSubtotal }}</span>
        </div>
      </section>

      <!-- Sticky Footer -->
      <footer class="pricing-footer">
        <div class="pricing-row">
          <span class="row-label">Base</span>
          <span class="row-value">{{ formattedSubtotal }}</span>
        </div>
        <div class="footer-divider"></div>
        <div class="pricing-row total-row">
          <span>Estimated total</span>
          <span class="total-amount">{{ formattedSubtotal }}</span>
        </div>
        <p class="footer-disclaimer">Charged at pick-up. Final total may vary with actual add-ons selected at counter.</p>
        <button 
          v-if="isBookable" 
          class="btn-book" 
          @click="bookCar"
        >Book this car</button>
        <span v-else class="not-bookable-text">Call to book: 877-924-0303</span>
      </footer>
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "CarPricingBreakdownModal",
  props: {
    item: { type: Object, required: true },
    searchBody: { type: Object, required: true },
    pickupLocation: { type: Object, default: null },
  },
  data() {
    return {
      loading: true,
      hasError: false,
      policy: {},
      addOnItems: [],
      selectedAddOnsTotal: 0,
      addOnsExpanded: false,
      conversions: {
        '108': 'Surcharge',
        '013': 'Special equipment',
        '045': 'Tax',
        'COL': 'Collection',
        'COV': 'Coverage',
        'CPN': 'Coupon',
        'DEL': 'Delivery',
        'INS': 'Insurance'
      }
    };
  },
  computed: {
    vendorLogo() {
      return this.pickupLocation?.logo || null;
    },
    vendorName() {
      return this.pickupLocation?.name || this.$helpers?.capitalize(this.item.companyInfo?.name) || this.item.companyInfo?.name || "Rental";
    },
    formattedDateRange() {
      const pickup = this.item.pickupInfo?.dateTime?.date;
      const dropoff = this.item.dropoffInfo?.dateTime?.date;
      if (!pickup || !dropoff) return "";
      return `${moment(pickup).format("MMM D")} – ${moment(dropoff).format("MMM D")}`;
    },
    rentalDays() {
      const pickup = moment(this.item.pickupInfo?.dateTime?.date);
      const dropoff = moment(this.item.dropoffInfo?.dateTime?.date);
      return Math.max(1, dropoff.diff(pickup, "days"));
    },
    dailyRate() {
      return this.item.pricingInfo?.averagePricePerDay || 0;
    },
    totalPrice() {
      return this.item.pricingInfo?.total || 0;
    },
    currencyCode() {
      return this.item.pricingInfo?.currencyCode || "USD";
    },
    formattedDailyRate() {
      return this.$options.filters.formatPriceFromAmount(this.dailyRate.toFixed(0), 0, this.currencyCode);
    },
    formattedSubtotal() {
      const subtotal = this.dailyRate * this.rentalDays;
      return this.$options.filters.formatPriceFromAmount(subtotal.toFixed(0), 0, this.currencyCode);
    },
    taxItems() {
      const items = [];
      if (this.policy.rateDetails?.taxCovSurchargeGroup) {
        for (const group of this.policy.rateDetails.taxCovSurchargeGroup) {
          const charge = group.taxSurchargeCoverageInfo?.chargeDetails;
          if (charge?.comment && charge?.type === '045') {
            const name = charge.comment.split(' - ')[1] || charge.comment;
            items.push({
              name: name,
              amount: parseFloat(charge.amount) || 0,
              formattedAmount: this.$helpers.getCurrencySymbol(charge.currency) + charge.amount,
              note: null
            });
          }
        }
      }
      return items;
    },
    surchargeItems() {
      const items = [];
      if (this.policy.rateDetails?.taxCovSurchargeGroup) {
        for (const group of this.policy.rateDetails.taxCovSurchargeGroup) {
          const charge = group.taxSurchargeCoverageInfo?.chargeDetails;
          // Type '108' = Surcharge
          if (charge?.comment && charge?.type === '108') {
            const name = charge.comment.split(' - ')[1] || charge.comment;
            items.push({
              name: name,
              amount: parseFloat(charge.amount) || 0,
              formattedAmount: this.$helpers.getCurrencySymbol(charge.currency) + charge.amount,
            });
          }
        }
      }
      return items;
    },
    surchargesTotal() {
      return this.surchargeItems.reduce((sum, s) => sum + s.amount, 0);
    },
    formattedSurchargesSubtotal() {
      return this.$options.filters.formatPriceFromAmount(this.surchargesTotal.toFixed(0), 0, this.currencyCode);
    },
    formattedTaxes() {
      const taxes = this.totalPrice - (this.dailyRate * this.rentalDays);
      return this.$options.filters.formatPriceFromAmount(Math.max(0, taxes).toFixed(0), 0, this.currencyCode);
    },
    formattedTaxesSubtotal() {
      if (this.taxItems.length > 0) {
        const total = this.taxItems.reduce((sum, t) => sum + t.amount, 0);
        return this.$options.filters.formatPriceFromAmount(total.toFixed(0), 0, this.currencyCode);
      }
      return this.formattedTaxes;
    },
    addOnsTotal() {
      return this.addOnItems.reduce((sum, a) => sum + a.price, 0);
    },
    formattedAddOnsSubtotal() {
      return this.$options.filters.formatPriceFromAmount(this.addOnsTotal.toFixed(0), 0, this.currencyCode);
    },
    formattedAddOnsTotal() {
      return this.$options.filters.formatPriceFromAmount(this.selectedAddOnsTotal.toFixed(0), 0, this.currencyCode);
    },
    isBookable() {
      return this.item.isSelectable && this.item.immediateTravelBookingAdvice !== "prevent";
    },
  },
  methods: {
    async getRateInfo() {
      const raw = {
        stateful: true,
        sessionKey: null,
        normalizeResponse: false,
        pickupDate: this.item.pickupInfo.dateTime,
        dropoffDate: this.item.dropoffInfo.dateTime,
        companyCode: this.item.companyInfo?.code || this.item.vendorCode,
        pickupLocation: this.item.pickupInfo.locationKey?.split('-')[0] || this.item.pickupInfo.location,
        dropoffLocation: this.item.dropoffInfo.locationKey?.split('-')[0] || this.item.dropoffInfo.location,
        rateInformation: this.item.rateInformation,
        requestedCurrency: this.item.pricingInfo.currencyCode,
        vehicleInfo: {
          preferredVehicleType: this.item.carCode || this.item.vehicleInfo?.code
        }
      };
      try {
        const result = await this.$app.huddleLabsServerCall('amadeus/car/rate-info', raw);
        this.processResult(result);
      } catch {
        this.loading = false;
        this.hasError = true;
      }
    },
    processResult(result) {
      if (result.response) {
        if (result.response.errorWarning?.applicationError?.errorDetails?.errorCode === '99') {
          this.loading = false;
          this.hasError = true;
        } else {
          this.policy = result.response;
          this.buildAddOns();
          this.loading = false;
        }
      } else {
        this.loading = false;
        this.hasError = true;
      }
    },
    buildAddOns() {
      // Extract optional add-ons from coverage/insurance items
      if (this.policy.rateDetails?.taxCovSurchargeGroup) {
        for (const group of this.policy.rateDetails.taxCovSurchargeGroup) {
          const charge = group.taxSurchargeCoverageInfo?.chargeDetails;
          if (charge?.comment && (charge?.type === 'COV' || charge?.type === 'INS' || charge?.type === '013')) {
            const name = charge.comment.split(' - ')[1] || charge.comment;
            this.addOnItems.push({
              name: name,
              price: parseFloat(charge.amount) || 0,
              formattedPrice: this.$helpers.getCurrencySymbol(charge.currency) + charge.amount + '/day',
              description: '',
              recommended: charge.type === 'COV',
              selected: false,
              isFlat: false
            });
          }
        }
      }
    },
    recalculateTotal() {
      let total = 0;
      for (const addon of this.addOnItems) {
        if (addon.selected) {
          total += addon.isFlat ? addon.price : addon.price * this.rentalDays;
        }
      }
      this.selectedAddOnsTotal = total;
    },
    bookCar() {
      this.$emit("book", this.item);
      this.$emit("close");
    },
  },
  mounted() {
    this.getRateInfo();
  },
};
</script>

<style scoped>
.car-pricing-breakdown-modal {
  background: var(--color-background-primary, #fff);
  border-radius: 12px;
  border: 0.5px solid var(--color-border-tertiary, #e0e0e0);
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.loading-container,
.error-container {
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.error-message {
  text-align: center;
  color: var(--color-text-secondary, #666);
}

.error-message p {
  margin: 10px 0 16px;
  font-size: 14px;
}

.btn-close-error {
  padding: 8px 20px;
  border-radius: 8px;
  border: 0.5px solid var(--color-border-secondary, #ccc);
  background: none;
  cursor: pointer;
  font-size: 14px;
}

.modal-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 90vh;
}

/* Header */
.pricing-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 0.5px solid var(--color-border-tertiary, #e0e0e0);
  flex-shrink: 0;
  gap: 10px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-left > div {
  display: flex;
  align-items: center;
}

.vendor-logo-small {
  height: 24px;
  max-width: 80px;
  object-fit: contain;
  vertical-align: middle;
}

.vendor-name-small {
  font-weight: 500;
  font-size: 13px;
  color: var(--color-text-primary, #333);
}

.header-title {
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text-primary, #333);
}

.header-dates {
  font-size: 13px;
  color: var(--color-text-tertiary, #999);
  margin-right: auto;
}

/* Body */
.pricing-body {
  /* flex: 1; */
  overflow-y: auto;
  padding-bottom: 10px;
}

.pricing-section {
  padding: 1rem 1.25rem;
  border-bottom: 0.5px solid var(--color-border-tertiary, #e0e0e0);
}

.section-label {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.6px;
  color: var(--color-text-tertiary, #999);
  margin: 0 0 10px;
  text-transform: uppercase;
}

/* Collapsible Section Styles */
.collapsible-section .section-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  margin-bottom: 0;
}

.collapsible-section .section-header-row .section-label {
  margin-bottom: 0;
}

.toggle-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: var(--color-primary, #3498db);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.toggle-btn:hover {
  background-color: rgba(52, 152, 219, 0.1);
}

.collapsible-content {
  margin-top: 10px;
}

/* Collapse transition */
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}

.collapse-enter,
.collapse-leave-to {
  opacity: 0;
  max-height: 0;
}

.collapse-enter-to,
.collapse-leave {
  opacity: 1;
  max-height: 500px;
}

.pricing-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  margin-bottom: 6px;
}

.row-label {
  color: var(--color-text-secondary, #666);
}

.row-value {
  color: var(--color-text-primary, #333);
}

.row-label-with-note {
  display: flex;
  align-items: center;
  gap: 6px;
}

.row-note {
  font-size: 11px;
  color: var(--color-text-tertiary, #999);
}

.section-divider {
  height: 0.5px;
  background: var(--color-border-tertiary, #e0e0e0);
  margin: 8px 0;
}

.pricing-row.subtotal {
  font-weight: 500;
}

.pricing-row.subtotal span {
  color: var(--color-text-primary, #333);
}

/* Footer */
.pricing-footer {
  padding: 1rem 1.25rem;
  background: var(--color-background-secondary, #f9f9f9);
  border-top: 0.5px solid var(--color-border-tertiary, #e0e0e0);
  flex-shrink: 0;
}

.footer-divider {
  height: 0.5px;
  background: var(--color-border-secondary, #ccc);
  margin: 8px 0;
}

.total-row {
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 0;
}

.total-row span {
  color: var(--color-text-primary, #333);
}

.total-amount {
  font-size: 20px;
}

.footer-disclaimer {
  font-size: 11px;
  color: var(--color-text-tertiary, #999);
  margin: 6px 0 14px;
}

.btn-book {
  width: 100%;
  padding: 10px;
  background: #185FA5;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-book:hover {
  background: #134a82;
}

.not-bookable-text {
  display: block;
  text-align: center;
  font-size: 13px;
  color: #856404;
  font-weight: 500;
  padding: 10px 0;
}

/* Mobile Responsiveness */
@media screen and (max-width: 767px) {
  .car-pricing-breakdown-modal {
    max-width: 100%;
    border-radius: 16px 16px 0 0;
    max-height: calc(100vh - 40px);
    height: calc(100vh - 40px);
  }

  .modal-content {
    height: 100%;
    max-height: calc(100vh - 40px);
    margin: 0 !important;
  }

  .pricing-header {
    flex-wrap: wrap;
    padding: 0.875rem 1rem;
  }

  .header-left {
    flex-wrap: wrap;
    gap: 6px;
  }

  .header-title {
    font-size: 14px;
  }

  .header-dates {
    order: 3;
    width: 100%;
    margin-top: 6px;
    margin-right: 0;
    font-size: 12px;
  }

  .pricing-body {
    /* flex: 1; */
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    max-height: none;
    min-height: 0;
  }

  .pricing-section {
    padding: 0.875rem 1rem;
  }

  .section-label {
    font-size: 12px;
  }

  .row-label {
    font-size: 12px;
  }

  .row-value {
    font-size: 12px;
  }

  .pricing-footer {
    padding: 0.875rem 1rem;
    padding-bottom: calc(0.875rem + env(safe-area-inset-bottom, 0px));
    flex-shrink: 0;
    background: var(--color-background-secondary, #f9f9f9);
  }

  .total-row {
    font-size: 14px;
  }

  .total-amount {
    font-size: 18px;
  }

  .btn-book {
    padding: 14px;
    font-size: 15px;
  }

  .footer-disclaimer {
    font-size: 10px;
  }

  .vendor-logo-small {
    height: 20px;
  }
}

/* Extra small screens (375px and below) */
@media screen and (max-width: 414px) {
  .car-pricing-breakdown-modal {
    max-height: calc(100vh - 20px);
    height: calc(100vh - 20px);
  }

  .modal-content {
    max-height: calc(100vh - 20px);
  }

  .pricing-header {
    padding: 0.75rem 0.875rem;
  }

  .pricing-section {
    padding: 0.75rem 0.875rem;
  }

  .pricing-footer {
    padding: 0.75rem 0.875rem;
    padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px));
  }
}
</style>

